import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";

const TOOLBAR_GAP_PX = 6;
/** Room kept for the toolbar's width at the right edge. */
const TOOLBAR_ROOM_PX = 240;

/** Above the selection, left-aligned with it; below it when there is no room above. */
function toolbarPosition(selection: DOMRect): CSSProperties {
  const left = Math.max(TOOLBAR_GAP_PX, Math.min(selection.left, window.innerWidth - TOOLBAR_ROOM_PX));
  return selection.top > 48
    ? { left, bottom: window.innerHeight - selection.top + TOOLBAR_GAP_PX }
    : { left, top: selection.bottom + TOOLBAR_GAP_PX };
}

interface SelectionToolbarProps {
  /** Where the selected text is on screen. */
  selection: DOMRect;
  label: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A few actions over a text selection, in a portal so no scrolling pane clips it. Pressing
 * one keeps the selection it acts on; Escape closes it.
 */
export default function SelectionToolbar({ selection, label, onClose, children }: SelectionToolbarProps) {
  return createPortal(
    <div
      role="toolbar"
      aria-label={label}
      className="app-tooltip app-selection-toolbar"
      style={toolbarPosition(selection)}
      onPointerDown={(event) => { event.preventDefault(); }}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
